// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Dispatch } from 'redux';
import { patchJson } from 'Utils/http';
import type { RootState } from 'Store';
import { buildOrderContext } from 'Features/chatbot/chatOrder';
import { SetGeoDbElevationAction, SetOrderGeoidOffsetAction } from 'Features/order/orderActions';

/**
 * Persist the user's elevation nudge against ONE geodatabase, so it survives a remount and so a
 * user switching geodatabases within an order gets each one's own adjustment back. The value is a
 * delta on top of the order's automatic geoid correction, not the total shift.
 *
 * The order list is updated on success so a remount rehydrates from Redux without refetching; on
 * failure Redux is left alone, which keeps the stored value and the displayed value in agreement.
 * @returns true when the offset reached the server; false on any failure, logged here since a
 * failed save is otherwise invisible — the value looks applied in the viewer but is not persisted.
 */
export const SaveGdbElevationThunk = (gdbId: string, elevationOffsetZ: number) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<boolean> => {
    const context = buildOrderContext(getState());
    if (!context) {
      // eslint-disable-next-line no-console
      console.error('[SaveGdbElevationThunk] no order context — cannot save', { gdbId, elevationOffsetZ });
      return false;
    }

    const url = `${context.base}/orders/${context.orgId}/${context.projectId}/${context.orderId}/gdb-elevation/${encodeURIComponent(gdbId)}`;
    const result = await patchJson(url, { elevationOffsetZ }, context.token);
    if (!result.success) {
      // eslint-disable-next-line no-console
      console.error('[SaveGdbElevationThunk] save failed', { gdbId, elevationOffsetZ, message: result.message });
      return false;
    }

    dispatch(SetGeoDbElevationAction({ orderId: context.orderId, geoDbFileId: gdbId, elevationOffsetZ }));
    return true;
  };

// Why no correction is in force, so the UI (and its logs) can tell "this order legitimately has
// none" apart from "something went wrong and it's worth trying again" instead of showing the same
// silent, generic message for both.
export type EnsureGeoidOffsetReason = 'no-centroid' | 'lookup-failed' | 'request-failed';

export interface EnsureGeoidOffsetResult {
  applied: boolean;
  // Set only when applied is false.
  reason?: EnsureGeoidOffsetReason;
}

/**
 * Make sure the selected order carries a geoid correction, computing it server-side when it has
 * none. `geoidOffsetZ` is otherwise only written at KML-upload time, so an order whose lookup failed
 * then — or whose boundary predates the feature, or which is a corridor — stays uncorrected forever
 * and its data renders ~20-35 m off.
 *
 * The server never overwrites an existing value, so this is safe to call on any order.
 * @returns applied + reason — never a bare failure with no explanation.
 */
export const EnsureGeoidOffsetThunk = (orderId: string) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<EnsureGeoidOffsetResult> => {
    const context = buildOrderContext(getState());
    if (!context) return { applied: false, reason: 'request-failed' };

    const url = `${context.base}/orders/${context.orgId}/${context.projectId}/${orderId}/ensure-geoid-offset`;
    const result = await patchJson<{ geoidOffsetZ: number | null; reason?: 'no-centroid' | 'lookup-failed' }>(url, {}, context.token);

    if (!result.success) {
      // A genuine request failure (network, auth, 5xx) — distinct from the server legitimately
      // reporting that no correction exists. Logged so it is visible without reproducing the click.
      // eslint-disable-next-line no-console
      console.error('[EnsureGeoidOffsetThunk] request failed', { orderId, message: result.message });
      return { applied: false, reason: 'request-failed' };
    }

    if (typeof result.data?.geoidOffsetZ !== 'number') {
      // eslint-disable-next-line no-console
      console.warn('[EnsureGeoidOffsetThunk] no correction available', { orderId, reason: result.data?.reason });
      return { applied: false, reason: result.data?.reason ?? 'lookup-failed' };
    }

    dispatch(SetOrderGeoidOffsetAction({ orderId, geoidOffsetZ: result.data.geoidOffsetZ }));
    return { applied: true };
  };

export default SaveGdbElevationThunk;
